/**
 * Command - Base class for all undoable/redoable commands
 *
 * Implementation of the Command Pattern for undo/redo functionality.
 * All commands must implement execute() and undo() methods.
 *
 * @example
 * class CreateRectangleCommand extends Command {
 *   constructor(canvas, rect) {
 *     super();
 *     this.canvas = canvas;
 *     this.rect = rect;
 *   }
 *
 *   execute() {
 *     this.canvas.add(this.rect);
 *     this.canvas.renderAll();
 *   }
 *
 *   undo() {
 *     this.canvas.remove(this.rect);
 *     this.canvas.renderAll();
 *   }
 * }
 */
export class Command {
  /**
   * Execute the command (perform the action)
   * @throws {Error} If not implemented by subclass
   */
  execute() {
    throw new Error('Command.execute() must be implemented by subclass');
  }

  /**
   * Undo the command (reverse the action)
   * @throws {Error} If not implemented by subclass
   */
  undo() {
    throw new Error('Command.undo() must be implemented by subclass');
  }

  /**
   * Redo the command (re-execute after undo)
   * Default implementation: same as execute()
   */
  redo() {
    this.execute();
  }

  /**
   * Get a human-readable description of the command (for debugging)
   * @returns {string} Command description
   */
  getDescription() {
    return this.constructor.name;
  }
}
